/**
 * E013-S002: Hook to initialize the DatabaseDiscoveryBridge
 * E012-T015: Cross-Doc Relation Support
 * E012-T018: RBAC Database Visibility Filter
 *
 * This hook should be called once at app startup (e.g., in a root component)
 * to connect the Lit-based RelationConfigMenu with the React-based
 * DatabaseDiscoveryService and CrossDocRelationService.
 *
 * RBAC (E012-T018): Now provides both filtered and unfiltered lists to the bridge.
 * Cross-Doc (E012-T015): Provides CrossDocRelationService for bidirectional relations.
 *
 * Usage:
 * ```tsx
 * // In a root component that has access to services
 * function WorkspaceRoot() {
 *   useDatabaseDiscoveryBridge();
 *   return <children />;
 * }
 * ```
 */

import { useConfirmModal } from '@affine/component';
import { setBlockTrashGuard } from '@blocksuite/affine-block-streamlain';
import { useFramework, useService } from '@toeverything/infra';
import { useEffect } from 'react';

import { createDatabaseAppUsagePort } from '../../../../modules/streamlain/native-app/database-app-usage-port';
import { AutomationDiscoveryService } from '../../../../modules/streamlain/services/automation-discovery';
import { CrossDocRelationService } from '../../../../modules/streamlain/services/cross-doc-relation';
import { confirmDatabaseDelete } from '../../../../modules/streamlain/services/database-delete-confirm';
import { DatabaseDeletionService } from '../../../../modules/streamlain/services/database-deletion';
import { DatabaseDiscoveryService } from '../../../../modules/streamlain/services/database-discovery';
import { WorkspaceService } from '../../../../modules/workspace';
import {
  clearDatabaseDiscoverySource,
  findDatabaseByBlockId,
  setCrossDocRelationService,
  setDatabaseDiscoverySource,
  type WorkspaceDatabaseInfo,
} from './database-discovery-bridge';
import {
  getDatabaseSnapshot,
  getDiscoveryUpdateDetail,
} from './database-discovery-update';
import { createDatabaseTrashGuard } from './database-trash-guard';

type DatabaseDiscoveryLiveData = {
  value: WorkspaceDatabaseInfo[];
  subscribe: (callback: (databases: WorkspaceDatabaseInfo[]) => void) => {
    unsubscribe: () => void;
  };
};

function subscribeToDatabaseDiscoveryUpdates(
  source: DatabaseDiscoveryLiveData
): { unsubscribe: () => void } {
  let previousSnapshot = getDatabaseSnapshot(source.value);
  return source.subscribe(databases => {
    const nextSnapshot = getDatabaseSnapshot(databases);
    const detail = getDiscoveryUpdateDetail(previousSnapshot, nextSnapshot);
    previousSnapshot = nextSnapshot;
    if (
      detail.affectedDatabaseKeys.length === 0 &&
      detail.affectedDatabaseIds.length === 0
    ) {
      return;
    }
    window.dispatchEvent(
      new CustomEvent('database-discovery-updated', { detail })
    );
  });
}

// E018-S008-T005: Extend window for global bridge access
// E018-S008-T006: Extended for DB-Move support
declare global {
  interface Window {
    __databaseBridge?: {
      findDatabaseByBlockId: typeof findDatabaseByBlockId;
      // Column lookup for automation canvas display
      getColumnsForDatabase?: (
        blockId: string
      ) => Array<{ id: string; name: string; type: string }> | null;
      // E018-S008-T006: For DB-Move feature
      findIncomingRelations?: (targetDatabaseId: string) => unknown[];
      refresh?: () => void;
      findAutomationsReferencingDatabase?: (
        databaseBlockId: string
      ) => Array<{ docId: string; blockId: string }>;
    };
    /** Bridge for Lit components to show AFFiNE's native ConfirmModal */
    __confirmModal?: ReturnType<typeof useConfirmModal>;
  }
}

/**
 * Hook to initialize the database discovery bridge.
 * Connects the Lit-based RelationConfigMenu with the workspace-wide
 * DatabaseDiscoveryService and CrossDocRelationService.
 *
 * E012-T015: Provides CrossDocRelationService for cross-doc relations.
 * E012-T018: Provides both RBAC-filtered and unfiltered lists.
 *
 * Should be called once in a component that:
 * 1. Has access to the workspace scope
 * 2. Mounts early in the app lifecycle
 */
export function useDatabaseDiscoveryBridge(): void {
  const discoveryService = useService(DatabaseDiscoveryService);
  const automationDiscoveryService = useService(AutomationDiscoveryService);
  const crossDocRelationService = useService(CrossDocRelationService);
  const databaseDeletion = useService(DatabaseDeletionService);
  const workspace = useService(WorkspaceService).workspace;
  const framework = useFramework();
  const confirmModal = useConfirmModal();

  useEffect(() => {
    // Initialize the bridge with all LiveData sources
    // E012-T018: Include RBAC-filtered lists for UI components
    setDatabaseDiscoverySource(
      discoveryService.databases$,
      discoveryService.databasesByDoc$,
      discoveryService.accessibleDatabases$,
      discoveryService.accessibleDatabasesByDoc$,
      discoveryService.writableDatabases$,
      discoveryService.writableDatabasesByDoc$,
      discoveryService.accessibleTrashcanDatabases$
    );

    // E012-T015: Set cross-doc relation service for bidirectional relations
    setCrossDocRelationService(crossDocRelationService);

    // E018-S008-T005: Expose bridge on window for Lit components (Canvas Node)
    // E018-S008-T006: Extended for DB-Move feature
    // This allows runtime database name lookup without React context
    window.__databaseBridge = {
      findDatabaseByBlockId,
      // Column lookup for automation canvas display
      getColumnsForDatabase: (blockId: string) => {
        const db = findDatabaseByBlockId(blockId);
        if (!db) return null;
        return db.columns.map(c => ({ id: c.id, name: c.name, type: c.type }));
      },
      // E018-S008-T006: For DB-Move - find all DBs with relations pointing to a target
      findIncomingRelations: (targetDatabaseId: string) =>
        discoveryService.findIncomingRelations(targetDatabaseId),
      // E018-S008-T006: For DB-Move - refresh cache after move
      refresh: () => discoveryService.refresh(),
      // E018-S008-T006: For DB-Move - find automations referencing a database
      findAutomationsReferencingDatabase: (databaseBlockId: string) =>
        automationDiscoveryService.findAutomationsReferencingDatabase(
          databaseBlockId
        ),
    };

    // Expose confirmModal for Lit components (e.g., RelationConfigMenu dialogs)
    window.__confirmModal = confirmModal;

    // The trash asks once: a cloud table goes to the server's queue, a local
    // one to the doc's trash. A permanent-delete preview would wait for sync
    // and a workspace-wide reverse index before even asking.
    const trashGuardWorkspace = workspace.docCollection;
    setBlockTrashGuard(
      trashGuardWorkspace,
      'database',
      createDatabaseTrashGuard({
        local: workspace.flavour === 'local',
        port: createDatabaseAppUsagePort(framework),
        confirm: request => confirmDatabaseDelete(confirmModal, request),
        docTitle: docId =>
          discoveryService.databases$.value.find(
            database => database.docId === docId
          )?.docTitle ?? '',
        requestTrash: (target, visibility) =>
          databaseDeletion.requestTrash(target, visibility),
      })
    );

    // E018-S008-T005: Subscribe to database changes and emit event for Lit components
    // This triggers re-render of Canvas Nodes when database names or
    // permission-filtered access snapshots change.
    const subscriptions = [
      discoveryService.databases$,
      discoveryService.accessibleDatabases$,
      discoveryService.writableDatabases$,
      discoveryService.accessibleTrashcanDatabases$,
    ].map(source$ => subscribeToDatabaseDiscoveryUpdates(source$));

    return () => {
      // Cleanup on unmount
      for (const subscription of subscriptions) {
        subscription.unsubscribe();
      }
      clearDatabaseDiscoverySource();
      setBlockTrashGuard(trashGuardWorkspace, 'database', null);
      delete window.__databaseBridge;
      delete window.__confirmModal;
    };
  }, [
    automationDiscoveryService,
    discoveryService,
    crossDocRelationService,
    confirmModal,
    databaseDeletion,
    workspace,
    framework,
  ]);
}
